import React, { useEffect, useState, useRef } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { qrService } from '../../services/qrService';
import { shareService } from '../../services/shareService';
import { nfcService } from '../../services/nfcService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { getAppearanceStyles } from '../../utils/appearance';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

export const ShareModal = ({ card, user, onClose }) => {
  const { profile, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('link');
  const [qr, setQr] = useState(null);
  const [history, setHistory] = useState([]);
  const [nfcLink, setNfcLink] = useState(null);
  const [publishedCards, setPublishedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingNfc, setLoadingNfc] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrLabel, setQrLabel] = useState('');
  
  // NFC View States
  const [showNfcDisableConfirm, setShowNfcDisableConfirm] = useState(false);
  const [showNfcRegenerateConfirm, setShowNfcRegenerateConfirm] = useState(false);
  const [showNfcReassign, setShowNfcReassign] = useState(false);

  const qrRef = useRef(null);
  const imagePreviewRef = useRef(null);
  const signatureRef = useRef(null);

  const publicUrl = `${window.location.origin}${window.location.pathname}#/c/${card.public_slug}`;
  const qrUrl = qr ? `${window.location.origin}${window.location.pathname}#/q/${qr.token}` : publicUrl;
  const nfcUrl = nfcLink ? `${window.location.origin}${window.location.pathname}#/n/${nfcLink.token}` : '';

  const isDefault = profile?.default_card_id === card.id;
  const s = getAppearanceStyles(card.appearance);

  useEffect(() => {
    const initShareData = async () => {
      if (card.status !== 'published') {
        setLoading(false);
        return;
      }
      try {
        const [qrData, historyData, nfcData, publishedData] = await Promise.all([
          qrService.getOrCreateQr(card.id, user.id),
          shareService.getHistory(card.id, user.id),
          nfcService.getNfcLink(card.id, user.id),
          supabase.from('cards').select('id, internal_name, public_slug').eq('user_id', user.id).eq('status', 'published')
        ]);
        setQr(qrData);
        setHistory(historyData);
        setNfcLink(nfcData);
        setPublishedCards(publishedData.data || []);
      } catch (err) {
        console.error('Failed to load sharing data:', err);
      } finally {
        setLoading(false);
      }
    };
    initShareData();
  }, [card, user]);

  const handleCopyLink = (urlToCopy, type = 'link') => {
    navigator.clipboard.writeText(urlToCopy);
    setCopied(true);
    shareService.logShare(card.id, user.id, card.public_slug, `${type}_copied`);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = async (format) => {
    try {
      if (format === 'png') {
        const dataUrl = await toPng(qrRef.current, { quality: 1, pixelRatio: 3, backgroundColor: '#ffffff' });
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${card.public_slug}-qr.png`;
        link.click();
      } else if (format === 'svg') {
        const svgNode = document.getElementById('qr-svg-export');
        const svgData = new XMLSerializer().serializeToString(svgNode);
        const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${card.public_slug}-qr.svg`;
        link.click();
        URL.revokeObjectURL(url);
      } else if (format === 'pdf') {
        const dataUrl = await toPng(qrRef.current, { quality: 1, pixelRatio: 3, backgroundColor: '#ffffff' });
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [100, 120] });
        pdf.addImage(dataUrl, 'PNG', 10, 10, 80, 100);
        pdf.save(`${card.public_slug}-qr.pdf`);
      }
      shareService.logShare(card.id, user.id, card.public_slug, `qr_download_${format}`);
    } catch (err) {
      console.error('Failed to download QR:', err);
    }
  };

  const handleDownloadCardImage = async () => {
    try {
      const dataUrl = await toPng(imagePreviewRef.current, { quality: 1, pixelRatio: 2, useCORS: true });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${card.public_slug}-digital-card.png`;
      link.click();
      shareService.logShare(card.id, user.id, card.public_slug, 'card_image_downloaded');
    } catch (err) {
      console.error('Failed to generate image:', err);
      alert('Failed to generate image. Please ensure your profile photo is accessible.');
    }
  };

  const handleCopySignature = (type) => {
    if (type === 'rich') {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(signatureRef.current);
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand('copy');
      selection.removeAllRanges();
    } else {
      const html = signatureRef.current.outerHTML;
      navigator.clipboard.writeText(html);
    }
    shareService.logShare(card.id, user.id, card.public_slug, `signature_copied_${type}`);
    alert('Signature copied to clipboard!');
  };

  const handleSocialShare = (platform, url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    shareService.logShare(card.id, user.id, card.public_slug, `social_share`, platform);
  };

  const handleCopyMessage = (text) => {
    navigator.clipboard.writeText(text);
    shareService.logShare(card.id, user.id, card.public_slug, 'message_copied');
    alert('Message copied to clipboard!');
  };

  const toggleDefaultCard = async () => {
    try {
      const newDefault = isDefault ? null : card.id;
      await supabase.from('profiles').update({ default_card_id: newDefault }).eq('id', user.id);
      await refreshProfile();
    } catch (err) {
      console.error('Failed to update default card:', err);
    }
  };

  // ----- NFC Handlers -----
  const handleCreateNfc = async () => {
    setLoadingNfc(true);
    try {
      const newNfc = await nfcService.createNfcLink(card.id, user.id);
      setNfcLink(newNfc);
    } catch (err) {
      console.error(err);
      alert('Failed to create NFC link.');
    } finally {
      setLoadingNfc(false);
    }
  };

  const handleToggleNfcStatus = async () => {
    setLoadingNfc(true);
    try {
      const newStatus = nfcLink.status === 'active' ? 'disabled' : 'active';
      const updated = await nfcService.updateStatus(nfcLink.id, user.id, newStatus);
      setNfcLink(updated);
      setShowNfcDisableConfirm(false);
    } catch (err) {
      console.error(err);
      alert('Failed to update NFC status.');
    } finally {
      setLoadingNfc(false);
    }
  };

  const handleRegenerateNfc = async () => {
    setLoadingNfc(true);
    try {
      const updated = await nfcService.regenerateToken(nfcLink.id, user.id);
      setNfcLink(updated);
      setShowNfcRegenerateConfirm(false);
    } catch (err) {
      console.error(err);
      alert('Failed to regenerate NFC link.');
    } finally {
      setLoadingNfc(false);
    }
  };

  const handleReassignNfc = async (newCardId) => {
    setLoadingNfc(true);
    try {
      await nfcService.reassignCard(nfcLink.id, user.id, newCardId);
      // Link has moved to another card. Nullify it locally.
      setNfcLink(null);
      setShowNfcReassign(false);
    } catch (err) {
      console.error(err);
      alert('Failed to reassign NFC link.');
    } finally {
      setLoadingNfc(false);
    }
  };

  const encodedUrl = encodeURIComponent(publicUrl);
  const socialLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent("Let's connect: " + publicUrl)}`,
    x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent("Let's connect! " + publicUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    email: `mailto:?subject=${encodeURIComponent("My Digital Card")}&body=${encodeURIComponent("Here is my digital card: " + publicUrl)}`
  };

  const messages = [
    { name: 'Professional', text: `Thank you for your time. Here's my digital business card: ${publicUrl}` },
    { name: 'Friendly', text: `It was great meeting you today. Let's stay connected: ${publicUrl}` },
    { name: 'Speaker', text: `Thanks for attending my session. Here's how you can connect with me: ${publicUrl}` },
    { name: 'Simple', text: `Let's connect: ${publicUrl}` }
  ];

  const tabs = [
    { id: 'link', label: 'Link & QR', icon: FiIcons.FiLink },
    { id: 'nfc', label: 'NFC Tag', icon: FiIcons.FiSmartphone },
    { id: 'image', label: 'Digital Card', icon: FiIcons.FiImage },
    { id: 'signature', label: 'Email Signature', icon: FiIcons.FiMail },
    { id: 'social', label: 'Social & Messages', icon: FiIcons.FiMessageCircle },
    { id: 'history', label: 'History', icon: FiIcons.FiClock }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm sm:items-center items-end">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-5xl shadow-2xl animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-200 flex flex-col h-[90vh] sm:h-[85vh]">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center shrink-0 bg-slate-50 rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiIcons.FiShare2} className="text-xl" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-lg">Share Centre</h3>
              <p className="text-xs text-slate-500">{card.internal_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="hidden sm:flex items-center space-x-2 cursor-pointer bg-white px-3 py-1.5 rounded-lg border border-slate-200">
              <input 
                type="checkbox" 
                checked={isDefault} 
                onChange={toggleDefaultCard} 
                className="rounded border-slate-300 w-4 h-4 text-slate-900 focus:ring-slate-900"
              />
              <span className="text-sm font-medium text-slate-700">Default Card</span>
            </label>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors p-1 bg-white rounded-full border border-slate-200">
              <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
            </button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="w-full md:w-64 border-b md:border-b-0 md:border-r border-slate-100 bg-slate-50/50 flex flex-row md:flex-col overflow-x-auto md:overflow-y-auto hide-scrollbar p-4 gap-2 shrink-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? 'bg-white shadow-sm border border-slate-200 text-slate-900' 
                    : 'text-slate-600 hover:bg-slate-100 border border-transparent'
                }`}
              >
                <SafeIcon icon={tab.icon} className={`mr-3 text-lg ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-400'}`} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-white relative">
            {loading ? (
              <div className="flex justify-center py-20"><Spinner /></div>
            ) : card.status !== 'published' ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiIcons.FiEyeOff} className="text-2xl text-slate-400" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Card is not published</h4>
                <p className="text-sm text-slate-600">Publish your card from the editor to enable sharing features.</p>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto">
                
                {/* TAB: LINK & QR */}
                {activeTab === 'link' && (
                  <div className="space-y-8 animate-in fade-in duration-300">
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 mb-4">Your Public Link</h2>
                      <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl p-2 pl-4">
                        <span className="flex-1 text-sm text-slate-600 font-mono truncate mr-4">{publicUrl}</span>
                        <Button 
                          onClick={() => handleCopyLink(publicUrl, 'link')} 
                          variant={copied ? "primary" : "secondary"}
                          className={copied ? "bg-green-600 hover:bg-green-700 border-transparent text-white" : ""}
                        >
                          <SafeIcon icon={copied ? FiIcons.FiCheck : FiIcons.FiCopy} className="mr-2" />
                          {copied ? 'Copied!' : 'Copy Link'}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-slate-100">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900 mb-4">Download QR Code</h2>
                        <p className="text-sm text-slate-600 mb-6">Add an optional label to display below your QR code when downloading.</p>
                        <Input 
                          placeholder="e.g. Scan to Connect" 
                          value={qrLabel} 
                          onChange={(e) => setQrLabel(e.target.value)}
                          className="mb-6"
                        />
                        <div className="space-y-3">
                          <Button variant="secondary" className="w-full justify-start" onClick={() => handleDownloadQR('png')}>
                            <SafeIcon icon={FiIcons.FiImage} className="mr-3 text-slate-400" /> Download as PNG (Image)
                          </Button>
                          <Button variant="secondary" className="w-full justify-start" onClick={() => handleDownloadQR('svg')}>
                            <SafeIcon icon={FiIcons.FiMaximize} className="mr-3 text-slate-400" /> Download as SVG (Vector)
                          </Button>
                          <Button variant="secondary" className="w-full justify-start" onClick={() => handleDownloadQR('pdf')}>
                            <SafeIcon icon={FiIcons.FiFileText} className="mr-3 text-slate-400" /> Download as PDF (Print)
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-center justify-center bg-slate-50 rounded-2xl border border-slate-200 p-8">
                        {/* Hidden SVG for pure vector export */}
                        <div className="hidden">
                          <QRCodeSVG 
                            id="qr-svg-export"
                            value={qrUrl} 
                            size={1024} 
                            level="H" 
                            includeMargin={true}
                          />
                        </div>
                        
                        {/* Visible QR Container for PNG/PDF Export */}
                        <div ref={qrRef} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center">
                          <QRCodeCanvas 
                            value={qrUrl} 
                            size={180} 
                            level="H" 
                            includeMargin={false}
                            fgColor="#0f172a"
                          />
                          {qrLabel && (
                            <p className="mt-4 text-sm font-bold text-slate-900 text-center max-w-[180px] break-words">
                              {qrLabel}
                            </p>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-6 text-center">This QR code routes through Meetoi to track scan analytics automatically.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB: NFC */}
                {activeTab === 'nfc' && (
                  <div className="space-y-8 animate-in fade-in duration-300">
                    {/* Inline Modals for NFC Management */}
                    {showNfcDisableConfirm && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 backdrop-blur-sm px-4">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6 w-full max-w-sm">
                          <h3 className="text-lg font-bold text-slate-900 mb-2">Disable this NFC link?</h3>
                          <p className="text-sm text-slate-600 mb-6">The physical NFC tag will stop opening your Meetoi card until you reactivate or replace the link.</p>
                          <div className="flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => setShowNfcDisableConfirm(false)}>Cancel</Button>
                            <Button variant="danger" onClick={handleToggleNfcStatus} isLoading={loadingNfc}>Disable Link</Button>
                          </div>
                        </div>
                      </div>
                    )}
                    {showNfcRegenerateConfirm && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 backdrop-blur-sm px-4">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6 w-full max-w-sm">
                          <h3 className="text-lg font-bold text-slate-900 mb-2">Replace this NFC link?</h3>
                          <p className="text-sm text-slate-600 mb-6">Your current NFC link will stop working. You will need to write the new link onto your physical NFC card or tag.</p>
                          <div className="flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => setShowNfcRegenerateConfirm(false)}>Cancel</Button>
                            <Button variant="danger" onClick={handleRegenerateNfc} isLoading={loadingNfc}>Generate New Link</Button>
                          </div>
                        </div>
                      </div>
                    )}
                    {showNfcReassign && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/90 backdrop-blur-sm px-4">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6 w-full max-w-md">
                          <h3 className="text-lg font-bold text-slate-900 mb-2">Change Connected Card</h3>
                          <p className="text-sm text-slate-600 mb-6">Select a published card to open when this NFC link is tapped. The physical NFC tag does not need to be rewritten.</p>
                          <div className="space-y-2 mb-6 max-h-48 overflow-y-auto pr-2">
                            {publishedCards.filter(c => c.id !== card.id).map(c => (
                              <button 
                                key={c.id} 
                                onClick={() => handleReassignNfc(c.id)}
                                className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors"
                              >
                                <p className="font-bold text-slate-900">{c.internal_name}</p>
                                <p className="text-xs text-slate-500">meetoi.com/c/{c.public_slug}</p>
                              </button>
                            ))}
                            {publishedCards.filter(c => c.id !== card.id).length === 0 && (
                              <p className="text-sm text-slate-500 text-center py-4">No other published cards available.</p>
                            )}
                          </div>
                          <div className="flex justify-end gap-3">
                            <Button variant="ghost" onClick={() => setShowNfcReassign(false)}>Cancel</Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {!nfcLink ? (
                      <div className="text-center py-16 bg-slate-50 rounded-3xl border border-slate-200 border-dashed">
                        <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100">
                          <SafeIcon icon={FiIcons.FiSmartphone} className="text-4xl text-slate-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-3">Connect an NFC card or tag</h2>
                        <p className="text-slate-600 max-w-md mx-auto mb-8">
                          Create a secure Meetoi link and write it onto any compatible NFC card, sticker or tag.
                        </p>
                        <Button size="lg" onClick={handleCreateNfc} isLoading={loadingNfc}>
                          Create NFC Link
                        </Button>
                        <p className="text-xs text-slate-500 mt-4 font-medium">You will need a compatible NFC tag and an NFC-writing app.</p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {/* Left Column: Link & Stats */}
                          <div className="space-y-6">
                            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
                              <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold text-slate-900">NFC Link</h3>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${nfcLink.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                                  {nfcLink.status}
                                </span>
                              </div>
                              <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4">
                                <span className="text-sm text-slate-600 font-mono break-all">{nfcUrl}</span>
                              </div>
                              <div className="flex flex-wrap gap-3">
                                <Button 
                                  onClick={() => handleCopyLink(nfcUrl, 'nfc')} 
                                  variant={copied ? "primary" : "secondary"}
                                  className={copied ? "bg-green-600 hover:bg-green-700 border-transparent text-white flex-1" : "flex-1"}
                                >
                                  <SafeIcon icon={copied ? FiIcons.FiCheck : FiIcons.FiCopy} className="mr-2" />
                                  {copied ? 'Copied!' : 'Copy Link'}
                                </Button>
                                <Button variant="secondary" onClick={() => window.open(nfcUrl, '_blank')} className="flex-1">
                                  <SafeIcon icon={FiIcons.FiExternalLink} className="mr-2" /> Test Link
                                </Button>
                              </div>
                            </div>

                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                              <h3 className="font-bold text-slate-900 mb-4">Usage</h3>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Total Taps</p>
                                  <p className="text-2xl font-bold text-slate-900">{nfcLink.tap_count}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Last Tapped</p>
                                  <p className="text-sm font-medium text-slate-900 mt-1.5">
                                    {nfcLink.last_tapped_at ? new Date(nfcLink.last_tapped_at).toLocaleDateString() : 'Never'}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                              <h3 className="font-bold text-slate-900 mb-4">Management</h3>
                              <div className="space-y-3">
                                <Button variant="secondary" className="w-full justify-start" onClick={() => setShowNfcReassign(true)}>
                                  <SafeIcon icon={FiIcons.FiRepeat} className="mr-3 text-slate-400" /> Change Connected Card
                                </Button>
                                {nfcLink.status === 'active' ? (
                                  <Button variant="secondary" className="w-full justify-start text-amber-600 hover:bg-amber-50 hover:border-amber-200" onClick={() => setShowNfcDisableConfirm(true)}>
                                    <SafeIcon icon={FiIcons.FiPauseCircle} className="mr-3" /> Disable NFC Link
                                  </Button>
                                ) : (
                                  <Button variant="secondary" className="w-full justify-start text-green-600 hover:bg-green-50 hover:border-green-200" onClick={handleToggleNfcStatus} isLoading={loadingNfc}>
                                    <SafeIcon icon={FiIcons.FiPlayCircle} className="mr-3" /> Activate NFC Link
                                  </Button>
                                )}
                                <Button variant="secondary" className="w-full justify-start text-red-600 hover:bg-red-50 hover:border-red-200" onClick={() => setShowNfcRegenerateConfirm(true)}>
                                  <SafeIcon icon={FiIcons.FiRefreshCw} className="mr-3" /> Regenerate NFC Link
                                </Button>
                              </div>
                            </div>
                          </div>

                          {/* Right Column: Instructions */}
                          <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                              <SafeIcon icon={FiIcons.FiEdit3} className="text-2xl text-blue-400" />
                            </div>
                            <h3 className="text-xl font-bold mb-6">Write your Meetoi link to an NFC tag</h3>
                            <ol className="space-y-4 text-sm font-medium text-slate-300">
                              <li className="flex items-start">
                                <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mr-3 shrink-0 mt-0.5">1</span>
                                Copy your Meetoi NFC link.
                              </li>
                              <li className="flex items-start">
                                <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mr-3 shrink-0 mt-0.5">2</span>
                                Open an NFC-writing app on an NFC-enabled phone.
                              </li>
                              <li className="flex items-start">
                                <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mr-3 shrink-0 mt-0.5">3</span>
                                Choose to write a URL or URI record.
                              </li>
                              <li className="flex items-start">
                                <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mr-3 shrink-0 mt-0.5">4</span>
                                Paste your Meetoi NFC link.
                              </li>
                              <li className="flex items-start">
                                <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mr-3 shrink-0 mt-0.5">5</span>
                                Hold the NFC card or tag near the phone when prompted.
                              </li>
                              <li className="flex items-start">
                                <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mr-3 shrink-0 mt-0.5">6</span>
                                Test the tag with another compatible phone.
                              </li>
                            </ol>
                            <div className="mt-8 p-4 bg-white/5 rounded-xl border border-white/10 text-xs text-slate-400 leading-relaxed">
                              <p className="mb-2">The exact steps vary between phones and NFC-writing applications.</p>
                              <p className="mb-2">You may lock the physical tag after testing only if you understand that locking it can prevent rewriting. Meetoi cannot unlock a physically locked NFC tag.</p>
                              <p>Always test the link before relying on it at an event.</p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* TAB: DIGITAL CARD IMAGE */}
                {activeTab === 'image' && (
                  <div className="space-y-8 animate-in fade-in duration-300">
                    <div className="flex justify-between items-end mb-6">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">Digital Card Image</h2>
                        <p className="text-sm text-slate-600 mt-1">Export a beautiful graphic of your card for social media or presentations.</p>
                      </div>
                      <Button onClick={handleDownloadCardImage}>
                        <SafeIcon icon={FiIcons.FiDownload} className="mr-2" /> Download Image
                      </Button>
                    </div>

                    <div className="overflow-x-auto bg-slate-100 p-8 rounded-2xl border border-slate-200 flex justify-center">
                      <div 
                        ref={imagePreviewRef} 
                        className={`w-[600px] h-[315px] flex shadow-xl overflow-hidden relative ${s.cardBg} ${s.fontClass}`}
                        style={{ borderTop: `6px solid ${s.accentHex}` }}
                      >
                        <div className="flex-1 p-8 flex flex-col justify-center">
                          <h1 className={`text-3xl font-bold ${s.textMain} mb-1`}>{card.full_name}</h1>
                          <p className={`text-lg ${s.textMuted} font-medium mb-4`}>{card.job_title} {card.company_name ? `@ ${card.company_name}` : ''}</p>
                          <p className={`text-sm ${s.textMuted} mb-6 line-clamp-3`}>{card.bio}</p>
                          
                          <div className="mt-auto flex items-center">
                            <SafeIcon icon={FiIcons.FiLink} className="text-slate-400 mr-2" />
                            <span className={`text-sm font-bold ${s.textMain}`}>meetoi.com/c/{card.public_slug}</span>
                          </div>
                        </div>
                        <div className={`w-[240px] ${s.elementBg} border-l ${s.border} p-6 flex flex-col items-center justify-center`}>
                          <div className={`w-24 h-24 ${s.cornerAvatar} overflow-hidden ${s.cardBg} border-4 ${s.border} shadow-sm mb-6`}>
                            {card.profile_image_url ? (
                              <img src={card.profile_image_url} className="w-full h-full object-cover" crossOrigin="anonymous" />
                            ) : (
                              <div className={`w-full h-full flex items-center justify-center ${s.textMuted}`}><SafeIcon icon={FiIcons.FiUser} className="text-3xl" /></div>
                            )}
                          </div>
                          <div className={`p-2 rounded-xl shadow-sm border ${s.border} ${s.cardBg}`}>
                            <QRCodeCanvas 
                              value={qrUrl} 
                              size={120} 
                              level="H" 
                              includeMargin={false}
                              fgColor={s.accentHex}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB: EMAIL SIGNATURE */}
                {activeTab === 'signature' && (
                  <div className="space-y-8 animate-in fade-in duration-300">
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 mb-4">Email Signature</h2>
                      <p className="text-sm text-slate-600 mb-6">Copy this signature to use in Gmail, Outlook, Apple Mail, or your preferred client.</p>
                      
                      <div className="bg-white border text-left border-slate-200 rounded-2xl p-8 mb-6 shadow-sm overflow-x-auto">
                        {/* Signature HTML Structure */}
                        <div ref={signatureRef}>
                          <table cellPadding="0" cellSpacing="0" style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#333333', fontSize: '14px', lineHeight: '1.5' }}>
                            <tbody>
                              <tr>
                                {card.profile_image_url && (
                                  <td style={{ paddingRight: '20px', verticalAlign: 'top' }}>
                                    <img src={card.profile_image_url} alt={card.full_name} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />
                                  </td>
                                )}
                                <td style={{ borderLeft: card.profile_image_url ? `2px solid ${s.accentHex}` : 'none', paddingLeft: card.profile_image_url ? '20px' : '0', verticalAlign: 'top' }}>
                                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#000000', marginBottom: '2px' }}>{card.full_name}</div>
                                  <div style={{ fontSize: '14px', color: '#666666', marginBottom: '8px' }}>{card.job_title} {card.company_name ? `| ${card.company_name}` : ''}</div>
                                  <table cellPadding="0" cellSpacing="0" style={{ fontSize: '12px', color: '#666666', marginTop: '8px' }}>
                                    <tbody>
                                      {card.phone && <tr><td style={{ paddingBottom: '4px' }}><strong>P:</strong> <a href={`tel:${card.phone}`} style={{ color: '#666666', textDecoration: 'none' }}>{card.phone}</a></td></tr>}
                                      {card.email && <tr><td style={{ paddingBottom: '4px' }}><strong>E:</strong> <a href={`mailto:${card.email}`} style={{ color: '#666666', textDecoration: 'none' }}>{card.email}</a></td></tr>}
                                      {card.website && <tr><td style={{ paddingBottom: '4px' }}><strong>W:</strong> <a href={card.website} style={{ color: '#666666', textDecoration: 'none' }}>{card.website.replace(/^https?:\/\//, '')}</a></td></tr>}
                                    </tbody>
                                  </table>
                                  <div style={{ marginTop: '12px' }}>
                                    <a 
                                      href={publicUrl} 
                                      style={{ display: 'inline-block', padding: '8px 16px', backgroundColor: s.accentHex, color: '#ffffff', textDecoration: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}
                                    >
                                      View Digital Card
                                    </a>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-4">
                        <Button className="flex-1" onClick={() => handleCopySignature('rich')}>
                          <SafeIcon icon={FiIcons.FiCopy} className="mr-2" /> Copy for Gmail / Outlook
                        </Button>
                        <Button variant="secondary" className="flex-1" onClick={() => handleCopySignature('html')}>
                          <SafeIcon icon={FiIcons.FiCode} className="mr-2" /> Copy HTML Source
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB: SOCIAL & MESSAGES */}
                {activeTab === 'social' && (
                  <div className="space-y-10 animate-in fade-in duration-300">
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 mb-4">Quick Share</h2>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                        <button onClick={() => handleSocialShare('linkedin', socialLinks.linkedin)} className="flex flex-col items-center p-4 bg-slate-50 hover:bg-[#0a66c2]/10 hover:text-[#0a66c2] hover:border-[#0a66c2]/30 border border-slate-200 rounded-2xl transition-all">
                          <SafeIcon icon={FiIcons.FiLinkedin} className="text-2xl mb-2" />
                          <span className="text-xs font-bold">LinkedIn</span>
                        </button>
                        <button onClick={() => handleSocialShare('whatsapp', socialLinks.whatsapp)} className="flex flex-col items-center p-4 bg-slate-50 hover:bg-[#25D366]/10 hover:text-[#25D366] hover:border-[#25D366]/30 border border-slate-200 rounded-2xl transition-all">
                          <SafeIcon icon={FiIcons.FiMessageCircle} className="text-2xl mb-2" />
                          <span className="text-xs font-bold">WhatsApp</span>
                        </button>
                        <button onClick={() => handleSocialShare('x', socialLinks.x)} className="flex flex-col items-center p-4 bg-slate-50 hover:bg-slate-200 border border-slate-200 rounded-2xl transition-all">
                          <SafeIcon icon={FiIcons.FiTwitter} className="text-2xl mb-2" />
                          <span className="text-xs font-bold">X (Twitter)</span>
                        </button>
                        <button onClick={() => handleSocialShare('facebook', socialLinks.facebook)} className="flex flex-col items-center p-4 bg-slate-50 hover:bg-[#1877F2]/10 hover:text-[#1877F2] hover:border-[#1877F2]/30 border border-slate-200 rounded-2xl transition-all">
                          <SafeIcon icon={FiIcons.FiFacebook} className="text-2xl mb-2" />
                          <span className="text-xs font-bold">Facebook</span>
                        </button>
                        <button onClick={() => handleSocialShare('email', socialLinks.email)} className="flex flex-col items-center p-4 bg-slate-50 hover:bg-red-50 hover:text-red-500 hover:border-red-200 border border-slate-200 rounded-2xl transition-all col-span-2 sm:col-span-1">
                          <SafeIcon icon={FiIcons.FiMail} className="text-2xl mb-2" />
                          <span className="text-xs font-bold">Email</span>
                        </button>
                      </div>
                    </div>

                    <div>
                      <h2 className="text-xl font-bold text-slate-900 mb-4">Message Templates</h2>
                      <div className="space-y-4">
                        {messages.map((msg, idx) => (
                          <div key={idx} className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{msg.name}</span>
                              <button 
                                onClick={() => handleCopyMessage(msg.text)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-bold flex items-center"
                              >
                                <SafeIcon icon={FiIcons.FiCopy} className="mr-1" /> Copy
                              </button>
                            </div>
                            <p className="text-slate-700 text-sm">{msg.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB: HISTORY */}
                {activeTab === 'history' && (
                  <div className="space-y-6 animate-in fade-in duration-300">
                    <h2 className="text-xl font-bold text-slate-900 mb-2">Sharing History</h2>
                    <p className="text-sm text-slate-600 mb-6">A private log of when and how you've shared this card.</p>
                    
                    {history.length === 0 ? (
                      <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-200 border-dashed">
                        <SafeIcon icon={FiIcons.FiClock} className="text-3xl text-slate-400 mx-auto mb-3" />
                        <p className="text-slate-500 text-sm">No sharing history recorded yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {history.map(item => (
                          <div key={item.id} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl shadow-sm">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center mr-4 shrink-0">
                                <SafeIcon 
                                  icon={
                                    item.share_type.includes('qr') ? FiIcons.FiMaximize :
                                    item.share_type.includes('image') ? FiIcons.FiImage :
                                    item.share_type.includes('signature') ? FiIcons.FiMail :
                                    item.share_type.includes('social') ? FiIcons.FiMessageCircle :
                                    FiIcons.FiLink
                                  } 
                                  className="text-slate-500" 
                                />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-slate-900 capitalize">
                                  {item.share_type.replace(/_/g, ' ')} {item.platform ? `(${item.platform})` : ''}
                                </p>
                                <p className="text-xs text-slate-500">{new Date(item.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};